import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { parseMentionTokens, taskContextUrl } from '@shared/task-contexts';
import { tagRanges } from '@shared/task-tags';
import type { TaskContext, TaskMember } from '@shared/types/tasks';
import { cn } from '@/lib/utils';
import { memberHandle, memberName } from './task-ui';
import { ExternalLink } from '@/components/ExternalLink';
import { useAiBuilderMentions } from './ai-builder-mention';
import { TASK_LINK_FULL_MAX, taskLinkLabel } from './task-link-label';

const URL_RE = /(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/g;

/** `#tags` in plain text link to the Board filtered by that tag. */
function tagify(text: string, keyPrefix: string, onNavigate?: () => void): ReactNode[] {
  const parts: ReactNode[] = [];
  let cursor = 0;
  tagRanges(text).forEach((r, i) => {
    parts.push(text.slice(cursor, r.start));
    parts.push(
      <Link
        key={`${keyPrefix}-tag${i}`}
        to={`/tasks?tag=${encodeURIComponent(r.tag)}`}
        onClick={onNavigate}
        className="rounded-sm bg-muted px-0.5 text-foreground underline-offset-2 hover:underline"
      >
        {text.slice(r.start, r.end)}
      </Link>
    );
    cursor = r.end;
  });
  parts.push(text.slice(cursor));
  return parts;
}

function linkify(text: string, keyPrefix: string, onNavigate?: () => void): ReactNode[] {
  return text.split(URL_RE).map((part, i) =>
    i % 2 === 1 ? (
      <ExternalLink
        href={part}
        key={`${keyPrefix}-${i}`}
        icon={false}
        className="inline break-all"
        data-hint={part.length > TASK_LINK_FULL_MAX ? part : undefined}
      >
        {taskLinkLabel(part, window.location.origin)}
      </ExternalLink>
    ) : (
      <Fragment key={`${keyPrefix}-${i}`}>{tagify(part, `${keyPrefix}-${i}`, onNavigate)}</Fragment>
    )
  );
}

/** Renders stored text: people mentions in bold, thing mentions as links to their page, #tags as Board filters. */
export function MentionText({
  text,
  members,
  contexts = [],
  className,
  onNavigate,
  aiBuilder,
}: {
  text: string;
  members: TaskMember[];
  /** The task's Contexts: their stored URLs are exact (a batch product's includes its batch). */
  contexts?: TaskContext[];
  className?: string;
  onNavigate?: () => void;
  /** The team has the AI builder (T-393); else its mention is plain text. Defaults to the task's (AiBuilderMentions). */
  aiBuilder?: boolean;
}) {
  const aiOn = useAiBuilderMentions(aiBuilder);
  const tokens = parseMentionTokens(text);
  const parts: ReactNode[] = [];
  let cursor = 0;
  tokens.forEach((token, i) => {
    parts.push(...linkify(text.slice(cursor, token.start), `t${i}`, onNavigate));
    if (token.target.kind === 'user') {
      const id = token.target.id;
      const member = members.find((m) => m.user_id === id);
      parts.push(
        <span key={`m${i}`} className="font-medium text-primary" data-hint={member ? memberName(member) : undefined}>
          @{member ? memberHandle(member) : token.label}
        </span>
      );
    } else if (token.target.kind === 'ai') {
      parts.push(
        aiOn ? (
          <span key={`m${i}`} className="font-medium text-primary" data-hint="The task loop">
            @{token.label}
          </span>
        ) : (
          `@${token.label}`
        )
      );
    } else {
      const { ref } = token.target;
      const known = contexts.find((c) => c.type === ref.type && c.id === ref.id);
      parts.push(
        <Link
          key={`m${i}`}
          to={known?.url ?? taskContextUrl(ref)}
          onClick={onNavigate}
          className="font-medium text-primary underline-offset-2 hover:underline"
        >
          @{token.label}
        </Link>
      );
    }
    cursor = token.end;
  });
  parts.push(...linkify(text.slice(cursor), 'end', onNavigate));
  return <div className={cn('whitespace-pre-wrap break-words', className)}>{parts}</div>;
}
